import React, { useMemo } from 'react';
import { useProjectWithErrorHandling } from 'hooks/projects/useProjectWithErrorHandling';
import { useTranscriptSearch, useProjectSearchState } from 'hooks/search/queries';
import ProjectSearch from './ProjectSearch';
import ToTranscriptContentWrapper from './ToTranscriptContentWrapper';

function ProjectSearchContainer(props) {
  // Check if project exists and handle 404
  const { NotFoundComponent } = useProjectWithErrorHandling(props.projectID);
  
  // Use persisted search state for this project
  const {
    searchInput,
    setSearchInput,
    submittedSearchTerm,
    setSubmittedSearchTerm,
  } = useProjectSearchState(props.projectID);
  
  // Use React Query for search
  const { 
    data: searchData, 
    isLoading,
    isFetching,
    isError 
  } = useTranscriptSearch(props.projectID, submittedSearchTerm);
  
  // Process search results with transcript names
  const processedResults = useMemo(() => {
    if (!searchData || !searchData.results) {
      return [];
    }
    
    // Map the results and ensure transcript_id is present for compatibility
    return searchData.results.map((result) => ({
      ...result,
      transcript_id: result.id, // Map id to transcript_id for compatibility
      name: result.name || "Untitled"
    }));
  }, [searchData]);
  
  // Calculate count of results
  const resultsCount = useMemo(() => {
    if (!processedResults || processedResults.length === 0) {
      return 0;
    }
    
    return processedResults.reduce((total, result) => {
      const resultCount = result.results ? result.results.length : 0;
      return total + resultCount;
    }, 0);
  }, [processedResults]);
  
  const handleSearchTextChange = (event) => {
    setSearchInput(event.target.value);
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    // Only trigger search when form is submitted
    setSubmittedSearchTerm(searchInput);
  };
  
  if (NotFoundComponent) {
    return <NotFoundComponent />;
  }
  
  return (
    <ToTranscriptContentWrapper
      loaded={props.onboardingState.loaded}
      showOnboarding={props.onboardingState.hasNoCodeableData}
      header="Find what you're looking for"
      body="Search words or phrases across all transcripts in your project."
      intercomArticleId={process.env['INTERCOM_ARTICLE_SEARCH']}
      helpArticleText="Learn more about search"
      hasEditPermission={props.hasEditPermission}
      projectID={props.projectID}
    >
      <ProjectSearch
        results={processedResults}
        isLoading={isLoading}
        isFetching={isFetching}
        isError={isError}
        count={resultsCount}
        total={searchData?.total || 0}
          onSubmit={handleSubmit}
        searchTerm={submittedSearchTerm}
        searchText={searchInput}
        onSearchTextChange={handleSearchTextChange}
        projectID={props.projectID}
        hasEditPermission={props.hasEditPermission}
      />
    </ToTranscriptContentWrapper>
  );
}

export default ProjectSearchContainer;